import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { WorkoutUnityResult } from 'web_unity_engine';
import { IAchievement, IAchievementsData, IAchievementsGlobalStatsPayload, IDetectAchievementsPayload } from './types';
import { filterCurrentAchievements, sortAchievements } from './utils';

export interface IAchievementsState {
  loading: boolean;
  single: IAchievement[];
  multi: IAchievement[];
  exerciseId: string;
  score: number;
  current: IAchievement[];
  stats: {
    finishedCycles: number;
    finishedSessions: number;
    numberWorkouts: number;
  };
}

export const initialState: IAchievementsState = {
  loading: true,
  // full list of single achievements
  single: [],
  // full list of multi achievements
  multi: [],
  // current exercise id
  exerciseId: '',
  // current score
  score: 0,
  // achievements list for current result
  current: [],
  // global statistics
  stats: {
    finishedCycles: 0,
    finishedSessions: 0,
    numberWorkouts: 0,
  },
};

export const statePropName = 'achievements';

const achievementsSlice = createSlice({
  name: statePropName,
  initialState,
  reducers: {
    initAchievements(state, action: PayloadAction<number>) {
      return { ...initialState };
    },
    loadAchievements(state) {
      state.loading = true;
      state.current = [];
    },
    achievementsLoaded(state, action: PayloadAction<IAchievementsData>) {
      const { single, multi } = action.payload;
      state.single = single;
      state.multi = multi;
      state.loading = false;
    },
    achievementsReady(state, action: PayloadAction<IAchievementsData>) {
      const { single, multi } = action.payload;
      // remove lesser achievements of same type and sort global according to predefined order
      state.current = sortAchievements(filterCurrentAchievements([...single, ...multi]));
      state.loading = false;
    },
    detectAchievements: {
      reducer(state, action: PayloadAction<IDetectAchievementsPayload>) {
        state.loading = true;
        state.exerciseId = action.payload.exerciseId;
        state.score = action.payload.progress.stats.score;
        state.current = [];
      },
      prepare(
        workoutId: number,
        exerciseId: string,
        siblings: number[],
        progress: WorkoutUnityResult,
        maxDifficulty: number,
        comparison: number,
        totalGameplays: number,
        goal: number,
        bestResult?: number
      ) {
        return {
          payload: {
            workoutId,
            exerciseId,
            siblings,
            progress,
            maxDifficulty,
            comparison,
            totalGameplays,
            goal,
            bestResult,
          },
        };
      },
    },
    achievementsGlobalStats: {
      reducer(state, action: PayloadAction<IAchievementsGlobalStatsPayload>) {
        const { finishedCycles, finishedSessions, numberWorkouts } = action.payload;
        state.stats = { finishedCycles, finishedSessions, numberWorkouts };
      },
      prepare(
        currentCycleId: number,
        currentSessionId: number,
        cycleLength: number,
        finishedCycles: number,
        finishedSessions: number,
        numberWorkouts: number
      ) {
        return {
          payload: {
            currentCycleId,
            currentSessionId,
            cycleLength,
            finishedCycles,
            finishedSessions,
            numberWorkouts,
          },
        };
      },
    },
    storeAchievements(state, action: PayloadAction<IAchievementsData>) {
      const { single, multi } = action.payload;
      state.single = single;
      state.multi = multi;
    },
    hideAchievements(state) {},
    clearAchievements(state) {
      state.current = [];
    },
  },
});

export const AchievementsActions = achievementsSlice.actions;
export default achievementsSlice.reducer;
